import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  styled,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { stringSlugify } from '../common-utilities';
import { type SyntheticEvent, useState } from 'react';
import { HtmlTypography, TabPanel } from '@orcha/micro-library-core-ui';
import {
  customColorPalette,
  proAccountThemeCoreUI,
} from '@orcha/micro-library-core-ui';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

const a11yProps = (index: number) => {
  return {
    id: `score-details-tab-${index}`,
    'aria-controls': `score-details-tab-${index}`,
  };
};

type scoreDetailsData = {
  name?: string;
  htmlContent?: string;
  scoreValue?: number;
};

type ScoreDetailsViewProps = {
  eventHandlers: {
    handleScoreTabChange: (tabName: string) => void;
  };
  dynamicContent?: scoreDetailsData[];
  isLoading?: boolean;
  translatedText: {
    platformScoreDetailsHeading: string;
    platformScoreDetailsMissingDynamicContent: string;
  };
};

/**
 * ScoreDetailsView from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param ScoreDetailsViewProps
 * @returns
 */
export const ScoreDetailsView = ({
  eventHandlers,
  dynamicContent,
  isLoading,
  translatedText,
  ...props
}: ScoreDetailsViewProps) => {
  const [selectedTab, setSelectedTab] = useState(0);
  const { handleScoreTabChange } = eventHandlers ?? {};
  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);

    if (!handleScoreTabChange) {
      return;
    }

    const tabName = (_event.target as HTMLElement).textContent;
    if (tabName) {
      handleScoreTabChange(tabName);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const tabIndex = event.target.value as unknown as number;
    setSelectedTab(tabIndex);
  };

  const renderTab = (section: scoreDetailsData, index: number) => (
    <Tab
      key={section.name}
      label={section.name}
      data-testid={`app-details-score-details-tab-${
        section.name && stringSlugify(section.name)
      }`}
      {...a11yProps(index)}
      sx={{
        position: 'relative',
        ...(selectedTab !== index && {
          borderBottom: `1px solid ${customColorPalette.blueGreyMedium}`,
        }),
      }}
    />
  );

  const renderTabPanels = () => {
    const tabPanelStyle = {
      border: `1px solid ${customColorPalette.blueGreyMedium}`,
      borderRadius: '0 4px 4px 4px',
      padding: `${proAccountThemeCoreUI.spacing(
        2
      )} ${proAccountThemeCoreUI.spacing(4)} ${proAccountThemeCoreUI.spacing(
        4
      )}`,
      marginTop: '-1px',
    };

    const renderContent = (htmlContent?: string) => {
      if (isLoading) {
        return (
          <Box sx={{ display: 'flex' }}>
            <CircularProgress
              color="primary"
              size="6rem"
              sx={{ margin: '0 auto' }}
            />
          </Box>
        );
      }

      if (htmlContent && htmlContent.length > 0) {
        return <HtmlTypography bodyHtml={htmlContent} />;
      }

      return (
        <Alert severity="info">
          {translatedText?.platformScoreDetailsMissingDynamicContent}
        </Alert>
      );
    };

    return dynamicContent?.map((section, platformIndex) => (
      <TabPanel
        key={section.name}
        value={selectedTab}
        index={platformIndex}
        style={tabPanelStyle}
      >
        {renderContent(section.htmlContent)}
      </TabPanel>
    ));
  };

  return (
    <>
      {dynamicContent && (
        <Container {...props}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography component="h2" variant="h6">
              {translatedText?.platformScoreDetailsHeading}
            </Typography>
            <Box
              sx={{
                marginTop: (theme) => theme.spacing(4),
              }}
            >
              <Select
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'none',
                    marginBottom: (theme) => theme.spacing(2),
                  },
                }}
                value={selectedTab.toString()}
                onChange={handleSelectChange}
              >
                {dynamicContent.map((section, index) => (
                  <MenuItem key={section.name} value={index}>
                    {section.name}
                  </MenuItem>
                ))}
              </Select>
              <Tabs
                sx={{ display: { xs: 'none', sm: 'flex' } }}
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {dynamicContent.map((section, index) =>
                  renderTab(section, index)
                )}
              </Tabs>
              {renderTabPanels()}
            </Box>
          </Box>
        </Container>
      )}
    </>
  );
};
